import * as React from "react";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { TimePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import format from "date-fns/format";
import axios from "axios";
import Swal from "sweetalert2";
import Cookies from "js-cookie";
import BaseUrl from "../../Api/baseurl";
import { MdAdd } from "react-icons/md";

export default function FormAddDialog({ dateName, staffId, onSlotAdded }) {
    const [open, setOpen] = React.useState(false);
    const [formData, setFormData] = React.useState({
        startTime: null,
        endTime: null,
        timeBlock: "",
        noOfSlots: 1,
    });
    const [formError, setFormError] = React.useState({
        startTime: "",
        endTime: "",
        timeBlock: "",
        noOfSlots: "",
    });
    const [existingSlots, setExistingSlots] = React.useState([]);

    const timeBlockOptions = [
        { value: "morning", label: "Morning" },
        { value: "afternoon", label: "Afternoon" },
        { value: "evening", label: "Evening" },
    ];

    const handleClickOpen = async () => {
        await getData(dateName, staffId);
        setOpen(true);
    };

    const getData = async (dateName, staffId) => {
        try {
            const token = Cookies.get("token");
            const response = await axios.get(
                `${BaseUrl}/staff/slot-generation/${staffId}/`, // Updated to match POST endpoint
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            const daySlots = response.data.filter(
                slot => slot.day_of_week.toLowerCase() === dateName.toLowerCase()
            );
            setExistingSlots(daySlots);
        } catch (error) {
            console.error("Error fetching slot data:", error);
        }
    };

    const handleClose = () => {
        setOpen(false);
        setFormError({ startTime: "", endTime: "", timeBlock: "", noOfSlots: "" });
        setFormData({ startTime: null, endTime: null, timeBlock: "", noOfSlots: 1 });
    };

    const handleChange = (name, value) => {
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
        setFormError((prev) => ({
            ...prev,
            [name]: "",
        }));
    };

    const validateForm = () => {
        let isValid = true;
        const errors = { ...formError };

        if (!formData.startTime) {
            errors.startTime = "Please select a Start Time";
            isValid = false;
        }
        if (!formData.endTime) {
            errors.endTime = "Please select an End Time";
            isValid = false;
        }
        if (!formData.timeBlock) {
            errors.timeBlock = "Please select a Time Block";
            isValid = false;
        }
        if (!formData.noOfSlots || formData.noOfSlots < 1) {
            errors.noOfSlots = "Number of slots must be at least 1";
            isValid = false;
        }

        const start = formData.startTime ? format(formData.startTime, "HH:mm:ss") : null;
        const end = formData.endTime ? format(formData.endTime, "HH:mm:ss") : null;

        if (start && end && start >= end) {
            errors.endTime = "End time must be after start time";
            isValid = false;
        }

        if (existingSlots.length > 0 && start && end) {
            for (const slot of existingSlots) {
                const existingStart = slot.start_time;
                const existingEnd = slot.end_time;
                if (
                    (start >= existingStart && start < existingEnd) ||
                    (end > existingStart && end <= existingEnd) ||
                    (start <= existingStart && end >= existingEnd)
                ) {
                    errors.startTime = "Time overlaps with existing slots";
                    errors.endTime = "Time overlaps with existing slots";
                    isValid = false;
                    break;
                }
            }
        }

        setFormError(errors);
        return isValid;
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (validateForm()) {
            const slotDetails = {
                staff: staffId,
                day_of_week: dateName,
                time_block: formData.timeBlock,
                start_time: format(formData.startTime, "HH:mm:ss"),
                end_time: format(formData.endTime, "HH:mm:ss"),
                no_of_slots: formData.noOfSlots,
            };

            try {
                const token = Cookies.get("token");
                const response = await axios.post(
                    `${BaseUrl}/staff/slot-generation/${staffId}/`,
                    slotDetails, // Send slot object directly
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            "Content-Type": "application/json",
                        },
                    }
                );
                Swal.fire("Added!", "Your slot has been added.", "success");
                handleClose();
                if (onSlotAdded) onSlotAdded();
            } catch (error) {
                console.error("Error submitting slot:", error.response?.data || error.message);
                const errorMsg = error.response?.data
                    ? Object.entries(error.response.data)
                          .map(([key, value]) => `${key}: ${value.join(", ")}`)
                          .join("\n")
                    : error.message;
                Swal.fire("Error!", errorMsg, "error");
            }
        }
    };

    const getDisabledTimeBlocks = () => {
        return existingSlots.map(slot => slot.time_block);
    };

    return (
        <React.Fragment>
            <button
                className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-sm rounded-md flex items-center gap-1 transition-colors"
                onClick={handleClickOpen}
            >
                <MdAdd className="text-base" /> Add
            </button>
            <Dialog
                open={open}
                onClose={handleClose}
                PaperProps={{
                    component: "form",
                    onSubmit: handleSubmit,
                }}
            >
                <DialogTitle>New Slot</DialogTitle>
                <DialogContent>
                    <DialogContentText>Please Enter the Slot Details</DialogContentText>
                    <div className="flex flex-col py-4">
                        <div className="flex items-center w-full mb-4">
                            <TextField
                                label="Day"
                                value={dateName}
                                disabled
                                fullWidth
                            />
                        </div>
                        <div className="flex flex-col gap-4">
                            <FormControl fullWidth>
                                <InputLabel id="time-block-label">Time Block</InputLabel>
                                <Select
                                    labelId="time-block-label"
                                    value={formData.timeBlock}
                                    label="Time Block"
                                    onChange={(event) => handleChange("timeBlock", event.target.value)}
                                >
                                    <MenuItem value="">
                                        <em>Select time block</em>
                                    </MenuItem>
                                    {timeBlockOptions.map((option) => (
                                        <MenuItem
                                            key={option.value}
                                            value={option.value}
                                            disabled={getDisabledTimeBlocks().includes(option.value)}
                                        >
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {formError.timeBlock && (
                                    <span className="text-red-500 mt-2 text-sm">
                                        {formError.timeBlock}
                                    </span>
                                )}
                            </FormControl>
                            <div className="flex gap-4">
                                <div className="flex flex-col w-full">
                                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                                        <TimePicker
                                            label="Start Time"
                                            value={formData.startTime}
                                            ampm={false}
                                            onChange={(newValue) => handleChange("startTime", newValue)}
                                            slotProps={{ textField: { fullWidth: true } }}
                                        />
                                    </LocalizationProvider>
                                    {formError.startTime && (
                                        <span className="text-red-500 mt-2 text-sm">
                                            {formError.startTime}
                                        </span>
                                    )}
                                </div>
                                <div className="flex flex-col w-full">
                                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                                        <TimePicker
                                            label="End Time"
                                            value={formData.endTime}
                                            ampm={false}
                                            onChange={(newValue) => handleChange("endTime", newValue)}
                                            slotProps={{ textField: { fullWidth: true } }}
                                        />
                                    </LocalizationProvider>
                                    {formError.endTime && (
                                        <span className="text-red-500 mt-2 text-sm">
                                            {formError.endTime}
                                        </span>
                                    )}
                                </div>
                            </div>
                            <div className="flex w-full">
                                <TextField
                                    label="No. of Slots"
                                    type="number"
                                    value={formData.noOfSlots}
                                    onChange={(e) => handleChange("noOfSlots", parseInt(e.target.value))}
                                    fullWidth
                                    inputProps={{ min: 1 }}
                                    error={!!formError.noOfSlots}
                                    helperText={formError.noOfSlots}
                                />
                            </div>
                        </div>
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button type="submit">Add</Button>
                </DialogActions>
            </Dialog>
        </React.Fragment>
    );
}